<template>
  <b-modal
    v-model="showModal"
    size="lg"
    id="suppliers-modal"
    :title="edited.id > 0 ? 'Edit supplier details' : 'Add new supplier'"
    :ok-title="isFormLoading ? 'Saving...' : 'Save'"
    :ok-disabled="isFormLoading"
    cancel-variant="outline-secondary"
    @ok="onSubmit($event)"
  >
    <div
      v-if="isContentLoading"
      class="text-center pt-3"
    >
      <b-spinner label="Loading..." />
    </div>
    <b-form v-else>
      <b-row>
        <b-col cols="12">
          <b-form-group
            label="Name"
            label-for="supplier-name"
          >
            <b-form-input
              id="supplier-name"
              v-model="edited.name"
              placeholder="Name"
            />
            <small
              v-if="v$.edited?.name.$error"
              class="text-danger"
            >Name is required</small>
          </b-form-group>
          <b-form-group
            label="Description"
            label-for="supplier-description"
          >
            <b-form-textarea
              id="supplier-description"
              v-model="edited.description"
              placeholder="Description"
              rows="3"
              max-rows="6"
            />
          </b-form-group>
          <b-form-group
            label="Email"
            label-for="supplier-email"
          >
            <b-form-input
              id="supplier-email"
              v-model="edited.email"
              type="email"
              placeholder="Email"
            />
          </b-form-group>
          <b-form-group
            label="Phone"
            label-for="supplier-phone"
          >
            <b-form-input
              id="supplier-phone"
              v-model="edited.phone"
              placeholder="Phone"
            />
          </b-form-group>
          <b-form-group
            label="Website"
            label-for="supplier-website"
          >
            <b-form-input
              id="supplier-website"
              v-model="edited.website"
              placeholder="https://example.com"
            />
          </b-form-group>
          <b-form-group
            label="Image"
            label-for="supplier-image"
          >
            <b-form-file
              id="supplier-image"
              v-model="edited.image"
              placeholder="Choose a file or drop it here..."
              drop-placeholder="Drop file here..."
            />
            <img
              v-if="edited.image_preview"
              :src="imagePreviewUrl(edited.image_preview)"
              :style="{ width: '150px', border: '1px solid #DDDDDD' }"
              class="my-1"
              alt=""
            />
          </b-form-group>
          <b-form-group
            label="Manager name"
            label-for="supplier-manager-name"
          >
            <b-form-input
              id="supplier-manager-name"
              v-model="edited.manager_name"
              placeholder="Manager name"
            />
          </b-form-group>
          <b-form-group
            label="Manager phone"
            label-for="supplier-manager-phone"
          >
            <b-form-input
              id="supplier-manager-phone"
              v-model="edited.manager_phone"
              placeholder="Manager phone"
            />
          </b-form-group>
          <b-form-group
            label="Manager email"
            label-for="supplier-manager-email"
          >
            <b-form-input
              id="supplier-manager-email"
              v-model="edited.manager_email"
              type="email"
              placeholder="Manager email"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>
  </b-modal>
</template>

<script setup>
import { watch, ref, computed, reactive } from 'vue'
import { useStore } from 'vuex'
import {
  BCol,
  BForm,
  BFormFile,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BModal,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import useVuelidate from '@vuelidate/core'
import { minLength, required } from '@vuelidate/validators'
import { toastSuccess, toastFailure } from '@/libs/toastification'

const cloneObject = (object) => JSON.parse(JSON.stringify(object))

const defaultSupplier = {
  id: null,
  name: null,
  description: null,
  email: null,
  phone: null,
  website: null,
  image: null,
  image_preview: null,
  manager_name: null,
  manager_phone: null,
  manager_email: null,
}

const props = defineProps({
  supplier: {
    type: Object,
    default: null,
  },
  clickDate: {
    type: Date,
    required: true,
  },
})

const showModal = ref(false)
const isContentLoading = ref(true)
const isFormLoading = ref(false)

const store = useStore()

const supplierFromStore = computed(() => store.state.suppliers.supplier)

const edited = reactive(cloneObject(defaultSupplier))

const rules = {
  edited: {
    name: { required, minLength: minLength(2) },
  },
}
const v$ = useVuelidate(rules, { edited })

const imagePreviewUrl = (value) => {
  if (!value) return ''
  return value.includes('https') ? value : `//${value}`
}

watch(supplierFromStore, (newVal) => {
  if (newVal?.data && newVal.data.id > 0) {
    edited.id = newVal.data.id
    edited.name = newVal.data.name
    edited.description = newVal.data.description
    edited.email = newVal.data.email
    edited.phone = newVal.data.phone
    edited.website = newVal.data.website
    edited.image_preview = newVal.data.image
    edited.manager_name = newVal.data.manager_name
    edited.manager_phone = newVal.data.manager_phone
    edited.manager_email = newVal.data.manager_email
  } else {
    Object.assign(edited, cloneObject(defaultSupplier))
  }
})

watch(
  () => props.clickDate,
  () => {
    if (props.supplier !== null) {
      const supplierId = props.supplier && props.supplier.id
      store.dispatch('suppliers/loadSupplier', supplierId)
        .then(() => {
          isContentLoading.value = false
          showModal.value = true
        })
        .catch((error) => {
          isContentLoading.value = false
          showModal.value = false
          toastFailure('Something went wrong: ' + error.message)
        })
    } else {
      Object.assign(edited, cloneObject(defaultSupplier))
      isContentLoading.value = false
      showModal.value = true
    }
  },
)

const onSubmit = async (event) => {
  if (isFormLoading.value) {
    return
  }

  event.preventDefault()
  isFormLoading.value = true
  const isFormCorrect = await v$.value.edited.$validate()
  if (!isFormCorrect) {
    isFormLoading.value = false
    return
  }

  await submitForm()
}

const submitForm = async () => {
  const formData = new FormData()

  const appendIfPresent = (key, value) => {
    if (value !== null && value !== undefined && value !== '') {
      formData.append(key, value)
    }
  }

  if (edited.id != null) {
    appendIfPresent('id', edited.id)
  }
  appendIfPresent('name', edited.name)
  appendIfPresent('description', edited.description)
  appendIfPresent('email', edited.email)
  appendIfPresent('phone', edited.phone)
  appendIfPresent('website', edited.website)
  appendIfPresent('manager_name', edited.manager_name)
  appendIfPresent('manager_phone', edited.manager_phone)
  appendIfPresent('manager_email', edited.manager_email)
  if (edited.image) {
    formData.append('image', edited.image)
  }

  if (edited.id != null) {
    formData.append('_method', 'PUT')

    store.dispatch('suppliers/updateSupplier', { formData })
      .then(() => {
        showModal.value = false
        toastSuccess('Successfully updated')
      })
      .catch(() => {
        toastFailure('Failed to update')
      })
      .finally(() => {
        isFormLoading.value = false
      })
  } else {
    store.dispatch('suppliers/addSupplier', { formData })
      .then(() => {
        showModal.value = false
        toastSuccess('Successfully added')
      })
      .catch(() => {
        toastFailure('Failed to add')
      })
      .finally(() => {
        isFormLoading.value = false
      })
  }

  edited.image = null
}

</script>
